/**
 * Shared player rename & merge helpers.
 *
 * Used both in-app (App.js handleUpdatePlayer) and offline (scripts/merge-player.mjs).
 * Pure functions: they accept doc data and return { updatedData, changed, changes }.
 * All array/object accesses are guarded so incomplete classic matches or legacy docs never throw.
 */

/**
 * Replaces oldName with newName in an array of strings or { name, ... } player objects.
 */
export function renameInPlayerList(list, oldName, newName) {
  if (!Array.isArray(list)) return { list, changed: false };
  let changed = false;
  const newList = list.map((item) => {
    if (typeof item === 'string') {
      if (item === oldName) {
        changed = true;
        return newName;
      }
      return item;
    }
    if (item && typeof item === 'object') {
      if (item.name === oldName) {
        changed = true;
        return { ...item, name: newName };
      }
    }
    return item;
  });
  return { list: newList, changed };
}

/**
 * Replaces oldName with newName in a team array (strings or objects).
 */
export function renameTeam(team, oldName, newName) {
  return renameInPlayerList(team, oldName, newName);
}

/**
 * Merges two player stats records (e.g. when combining Gait Barr + James E).
 */
export function mergePlayerStats(s1 = {}, s2 = {}) {
  const w = (s1.w ?? s1.wins ?? s1.matchesWon ?? 0) + (s2.w ?? s2.wins ?? s2.matchesWon ?? 0);
  const l = (s1.l ?? s1.losses ?? s1.matchesLost ?? 0) + (s2.l ?? s2.losses ?? s2.matchesLost ?? 0);
  const gw = (s1.gw ?? s1.gamesWon ?? 0) + (s2.gw ?? s2.gamesWon ?? 0);
  const gl = (s1.gl ?? s1.gamesLost ?? 0) + (s2.gl ?? s2.gamesLost ?? 0);
  const mvp = (s1.mvp ?? s1.mvpCount ?? 0) + (s2.mvp ?? s2.mvpCount ?? 0);
  const gamesPlayed = gw + gl;
  const matchesTotal = w + l;
  const winPct = matchesTotal > 0 ? w / matchesTotal : 0;
  return {
    ...s1,
    ...s2,
    w,
    l,
    wins: w,
    losses: l,
    matchesWon: w,
    matchesLost: l,
    gw,
    gl,
    gamesWon: gw,
    gamesLost: gl,
    gamesPlayed,
    mvp,
    mvpCount: mvp,
    winPct,
    gameDiff: gw - gl,
  };
}

/**
 * Re-keys an object keyed by player name (e.g. sessionStats or overall stats).
 * If newName already exists, merges their stats.
 */
export function renameInStatsMap(statsMap, oldName, newName) {
  if (!statsMap || typeof statsMap !== 'object') return { statsMap, changed: false };
  if (!Object.prototype.hasOwnProperty.call(statsMap, oldName)) return { statsMap, changed: false };

  const newMap = { ...statsMap };
  const oldStats = newMap[oldName];
  delete newMap[oldName];

  if (newMap[newName]) {
    newMap[newName] = mergePlayerStats(oldStats, newMap[newName]);
  } else {
    newMap[newName] = { ...oldStats };
  }
  return { statsMap: newMap, changed: true };
}

/**
 * Re-keys pairingStats (e.g. "Gait Barr & Ben" -> "Ben & James E").
 */
export function renameInPairingStats(pairingStats, oldName, newName) {
  if (!pairingStats || typeof pairingStats !== 'object') return { pairingStats, changed: false };

  let changed = false;
  const newMap = {};

  Object.entries(pairingStats).forEach(([pairKey, stats]) => {
    const pairNames = pairKey.split(' & ');
    let pairChanged = false;
    const mapped = pairNames.map((n) => {
      if (n === oldName) {
        pairChanged = true;
        return newName;
      }
      return n;
    }).sort();

    const newKey = mapped.join(' & ');
    if (pairChanged) changed = true;

    if (!newMap[newKey]) {
      newMap[newKey] = { ...stats };
    } else {
      newMap[newKey] = {
        wins: (newMap[newKey].wins || 0) + (stats.wins || 0),
        losses: (newMap[newKey].losses || 0) + (stats.losses || 0),
      };
    }
  });

  return { pairingStats: changed ? newMap : pairingStats, changed };
}

/**
 * Renames oldName to newName in a session doc (rotation, complete classic, incomplete classic).
 * Returns { updatedData, changed, changes }.
 */
export function renamePlayerInSession(sessionData, oldName, newName) {
  if (!sessionData || typeof sessionData !== 'object') return { updatedData: sessionData, changed: false, changes: [] };

  let changed = false;
  const changes = [];
  const updated = { ...sessionData };

  // 1. sessionPlayers
  if (Array.isArray(updated.sessionPlayers)) {
    const res = renameInPlayerList(updated.sessionPlayers, oldName, newName);
    if (res.changed) {
      updated.sessionPlayers = res.list;
      changed = true;
      changes.push('sessionPlayers');
    }
  }

  // 2. team1 & team2 (root level)
  if (Array.isArray(updated.team1)) {
    const res = renameTeam(updated.team1, oldName, newName);
    if (res.changed) {
      updated.team1 = res.list;
      changed = true;
      changes.push('team1');
    }
  }
  if (Array.isArray(updated.team2)) {
    const res = renameTeam(updated.team2, oldName, newName);
    if (res.changed) {
      updated.team2 = res.list;
      changed = true;
      changes.push('team2');
    }
  }

  // 3. winningTeam & losingTeam (root level, if present)
  if (Array.isArray(updated.winningTeam)) {
    const res = renameTeam(updated.winningTeam, oldName, newName);
    if (res.changed) {
      updated.winningTeam = res.list;
      changed = true;
      changes.push('winningTeam');
    }
  }
  if (Array.isArray(updated.losingTeam)) {
    const res = renameTeam(updated.losingTeam, oldName, newName);
    if (res.changed) {
      updated.losingTeam = res.list;
      changed = true;
      changes.push('losingTeam');
    }
  }

  // 4. mvp (string or array of strings)
  if (typeof updated.mvp === 'string') {
    if (updated.mvp === oldName) {
      updated.mvp = newName;
      changed = true;
      changes.push('mvp');
    } else if (updated.mvp.includes(oldName)) {
      // Possible comma/slash separated string
      const parts = updated.mvp.split(/,\s*/);
      if (parts.includes(oldName)) {
        updated.mvp = parts.map((p) => (p === oldName ? newName : p)).join(', ');
        changed = true;
        changes.push('mvp');
      }
    }
  } else if (Array.isArray(updated.mvp)) {
    const res = renameInPlayerList(updated.mvp, oldName, newName);
    if (res.changed) {
      updated.mvp = res.list;
      changed = true;
      changes.push('mvp');
    }
  }

  // 5. sitsOut
  if (typeof updated.sitsOut === 'string' && updated.sitsOut === oldName) {
    updated.sitsOut = newName;
    changed = true;
    changes.push('sitsOut');
  } else if (Array.isArray(updated.sitsOut)) {
    const res = renameInPlayerList(updated.sitsOut, oldName, newName);
    if (res.changed) {
      updated.sitsOut = res.list;
      changed = true;
      changes.push('sitsOut');
    }
  }

  // 6. sessionStats
  if (updated.sessionStats) {
    const res = renameInStatsMap(updated.sessionStats, oldName, newName);
    if (res.changed) {
      updated.sessionStats = res.statsMap;
      changed = true;
      changes.push('sessionStats');
    }
  }

  // 7. pairingStats
  if (updated.pairingStats) {
    const res = renameInPairingStats(updated.pairingStats, oldName, newName);
    if (res.changed) {
      updated.pairingStats = res.pairingStats;
      changed = true;
      changes.push('pairingStats');
    }
  }

  // 8. matchSummary (classic match)
  if (updated.matchSummary && typeof updated.matchSummary === 'object') {
    let summaryChanged = false;
    const ms = { ...updated.matchSummary };
    if (Array.isArray(ms.team1)) {
      const res = renameTeam(ms.team1, oldName, newName);
      if (res.changed) { ms.team1 = res.list; summaryChanged = true; }
    }
    if (Array.isArray(ms.team2)) {
      const res = renameTeam(ms.team2, oldName, newName);
      if (res.changed) { ms.team2 = res.list; summaryChanged = true; }
    }
    if (Array.isArray(ms.winningTeam)) {
      const res = renameTeam(ms.winningTeam, oldName, newName);
      if (res.changed) { ms.winningTeam = res.list; summaryChanged = true; }
    }
    if (Array.isArray(ms.losingTeam)) {
      const res = renameTeam(ms.losingTeam, oldName, newName);
      if (res.changed) { ms.losingTeam = res.list; summaryChanged = true; }
    }
    if (summaryChanged) {
      updated.matchSummary = ms;
      changed = true;
      changes.push('matchSummary');
    }
  }

  // 9. sessionHistory (games list)
  if (Array.isArray(updated.sessionHistory)) {
    let historyChanged = false;
    updated.sessionHistory = updated.sessionHistory.map((game) => {
      if (!game || typeof game !== 'object') return game;
      let gChanged = false;
      const newGame = { ...game };

      if (newGame.serving === oldName) {
        newGame.serving = newName;
        gChanged = true;
      }
      if (Array.isArray(newGame.winningTeam)) {
        const res = renameTeam(newGame.winningTeam, oldName, newName);
        if (res.changed) { newGame.winningTeam = res.list; gChanged = true; }
      }
      if (Array.isArray(newGame.losingTeam)) {
        const res = renameTeam(newGame.losingTeam, oldName, newName);
        if (res.changed) { newGame.losingTeam = res.list; gChanged = true; }
      }
      if (Array.isArray(newGame.team1)) {
        const res = renameTeam(newGame.team1, oldName, newName);
        if (res.changed) { newGame.team1 = res.list; gChanged = true; }
      }
      if (Array.isArray(newGame.team2)) {
        const res = renameTeam(newGame.team2, oldName, newName);
        if (res.changed) { newGame.team2 = res.list; gChanged = true; }
      }
      if (Array.isArray(newGame.players)) {
        const res = renameInPlayerList(newGame.players, oldName, newName);
        if (res.changed) { newGame.players = res.list; gChanged = true; }
      }

      if (gChanged) historyChanged = true;
      return newGame;
    });

    if (historyChanged) {
      changed = true;
      changes.push('sessionHistory');
    }
  }

  // 10. classicGameHistory (if present)
  if (Array.isArray(updated.classicGameHistory)) {
    let cHistoryChanged = false;
    updated.classicGameHistory = updated.classicGameHistory.map((game) => {
      if (!game || typeof game !== 'object') return game;
      let gChanged = false;
      const newGame = { ...game };

      if (newGame.serving === oldName) {
        newGame.serving = newName;
        gChanged = true;
      }
      if (Array.isArray(newGame.winningTeam)) {
        const res = renameTeam(newGame.winningTeam, oldName, newName);
        if (res.changed) { newGame.winningTeam = res.list; gChanged = true; }
      }
      if (Array.isArray(newGame.losingTeam)) {
        const res = renameTeam(newGame.losingTeam, oldName, newName);
        if (res.changed) { newGame.losingTeam = res.list; gChanged = true; }
      }
      if (Array.isArray(newGame.team1)) {
        const res = renameTeam(newGame.team1, oldName, newName);
        if (res.changed) { newGame.team1 = res.list; gChanged = true; }
      }
      if (Array.isArray(newGame.team2)) {
        const res = renameTeam(newGame.team2, oldName, newName);
        if (res.changed) { newGame.team2 = res.list; gChanged = true; }
      }

      if (gChanged) cHistoryChanged = true;
      return newGame;
    });

    if (cHistoryChanged) {
      changed = true;
      changes.push('classicGameHistory');
    }
  }

  // 11. scoringEvents
  if (Array.isArray(updated.scoringEvents)) {
    let eventsChanged = false;
    updated.scoringEvents = updated.scoringEvents.map((evt) => {
      if (!evt || typeof evt !== 'object') return evt;
      let eChanged = false;
      const newEvt = { ...evt };
      if (newEvt.player === oldName) { newEvt.player = newName; eChanged = true; }
      if (newEvt.server === oldName) { newEvt.server = newName; eChanged = true; }
      if (newEvt.receiver === oldName) { newEvt.receiver = newName; eChanged = true; }
      if (eChanged) eventsChanged = true;
      return newEvt;
    });
    if (eventsChanged) {
      changed = true;
      changes.push('scoringEvents');
    }
  }

  // 12. gameRotation (rotation sessions in sessions/current or scheduled)
  if (Array.isArray(updated.gameRotation)) {
    let rotChanged = false;
    updated.gameRotation = updated.gameRotation.map((game) => {
      if (!game || typeof game !== 'object') return game;
      let gChanged = false;
      const newGame = { ...game };

      if (Array.isArray(newGame.team1)) {
        const res = renameTeam(newGame.team1, oldName, newName);
        if (res.changed) { newGame.team1 = res.list; gChanged = true; }
      }
      if (Array.isArray(newGame.team2)) {
        const res = renameTeam(newGame.team2, oldName, newName);
        if (res.changed) { newGame.team2 = res.list; gChanged = true; }
      }
      if (typeof newGame.sitsOut === 'string' && newGame.sitsOut === oldName) {
        newGame.sitsOut = newName;
        gChanged = true;
      } else if (Array.isArray(newGame.sitsOut)) {
        const res = renameInPlayerList(newGame.sitsOut, oldName, newName);
        if (res.changed) { newGame.sitsOut = res.list; gChanged = true; }
      }
      if (Array.isArray(newGame.players)) {
        const res = renameInPlayerList(newGame.players, oldName, newName);
        if (res.changed) { newGame.players = res.list; gChanged = true; }
      }

      if (gChanged) rotChanged = true;
      return newGame;
    });

    if (rotChanged) {
      changed = true;
      changes.push('gameRotation');
    }
  }

  return { updatedData: updated, changed, changes };
}

/**
 * Renames oldName to newName in an event (season) doc.
 */
export function renamePlayerInEvent(eventData, oldName, newName) {
  if (!eventData || typeof eventData !== 'object') return { updatedData: eventData, changed: false, changes: [] };

  let changed = false;
  const changes = [];
  const updated = { ...eventData };

  // eventMvp: { player, tiedPlayers }
  if (updated.eventMvp && typeof updated.eventMvp === 'object') {
    let mvpChanged = false;
    const mvp = { ...updated.eventMvp };
    if (mvp.player === oldName) {
      mvp.player = newName;
      mvpChanged = true;
    }
    if (Array.isArray(mvp.tiedPlayers)) {
      const res = renameInPlayerList(mvp.tiedPlayers, oldName, newName);
      if (res.changed) {
        mvp.tiedPlayers = res.list;
        mvpChanged = true;
      }
    }
    if (mvpChanged) {
      updated.eventMvp = mvp;
      changed = true;
      changes.push('eventMvp');
    }
  }

  // participants
  if (Array.isArray(updated.participants)) {
    const res = renameInPlayerList(updated.participants, oldName, newName);
    if (res.changed) {
      updated.participants = res.list;
      changed = true;
      changes.push('participants');
    }
  }

  // players
  if (Array.isArray(updated.players)) {
    const res = renameInPlayerList(updated.players, oldName, newName);
    if (res.changed) {
      updated.players = res.list;
      changed = true;
      changes.push('players');
    }
  }

  return { updatedData: updated, changed, changes };
}

/**
 * Renames oldName to newName in stats/overall doc.
 */
export function renamePlayerInOverallStats(overallStats, oldName, newName) {
  return renameInStatsMap(overallStats, oldName, newName);
}

/**
 * Renames oldName to newName in a club doc (players list and members map).
 * Handles photo-only edits (oldName === newName with newPhotoUrl).
 */
export function renamePlayerInClub(clubData, oldName, newName, newPhotoUrl = null) {
  if (!clubData || typeof clubData !== 'object') return { updatedData: clubData, changed: false, changes: [] };

  let changed = false;
  const changes = [];
  const updated = { ...clubData };
  const isNameChange = oldName !== newName;

  // players array
  if (Array.isArray(updated.players)) {
    let playerFound = false;
    let photoUpdated = false;

    // Only deduplicate if the name is actually changing to an already-existing name
    const existingNewIndex = isNameChange
      ? updated.players.findIndex((p) => (typeof p === 'string' ? p : p?.name) === newName)
      : -1;

    const newPlayers = [];
    for (const p of updated.players) {
      const pName = typeof p === 'string' ? p : p?.name;
      if (pName === oldName) {
        playerFound = true;
        // If name changed AND newName already exists in another slot in the list, omit oldName to merge
        if (existingNewIndex !== -1) {
          continue;
        }

        // Update player
        if (typeof p === 'string') {
          newPlayers.push(newName);
        } else {
          const finalPhoto = newPhotoUrl !== null ? newPhotoUrl : (p.photoUrl || '');
          if (finalPhoto !== (p.photoUrl || '')) photoUpdated = true;
          newPlayers.push({
            ...p,
            name: newName,
            photoUrl: finalPhoto,
          });
        }
      } else {
        // Different player
        if (pName === newName && newPhotoUrl !== null && typeof p === 'object' && p.photoUrl !== newPhotoUrl) {
          newPlayers.push({ ...p, photoUrl: newPhotoUrl });
          photoUpdated = true;
        } else {
          newPlayers.push(p);
        }
      }
    }

    if ((isNameChange && playerFound) || photoUpdated) {
      updated.players = newPlayers;
      changed = true;
      changes.push('players');
    }
  }

  // members map
  if (updated.members && typeof updated.members === 'object') {
    let membersChanged = false;
    const newMembers = { ...updated.members };
    Object.entries(newMembers).forEach(([uid, m]) => {
      if (m && m.name === oldName) {
        const finalPhoto = newPhotoUrl !== null ? newPhotoUrl : (m.photoUrl || '');
        if (isNameChange || finalPhoto !== (m.photoUrl || '')) {
          newMembers[uid] = {
            ...m,
            name: newName,
            photoUrl: finalPhoto,
          };
          membersChanged = true;
        }
      }
    });
    if (membersChanged) {
      updated.members = newMembers;
      changed = true;
      changes.push('members');
    }
  }

  return { updatedData: updated, changed, changes };
}
